import '../../../../components/reports/generic-table/generic-table.component.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FILTER_NAMES } from '../../../../components/filters/hooks/filter-names.constants';
import {
  AppTabularDto,
  AppTabularColumn,
} from '../../../../components/reports/dtos/app-tabular.dto';
import { CommonTableComponentProps } from '../../../../components/reports/generic-table/generic-table.component';
import { ReportsComponentProps } from '../../../../components/reports/interfaces/reports-component-props';
import {
  useDataProvider,
  useDataProviderPaginated,
} from '../../../../hooks/data-provider/data-provider.hook';
import { SortBy } from '../../../../interfaces/sort-by.interface';
import { DataTableFlatWithHideRowsComponent } from './data-table-flat-with-hide-rows.component';

interface TableWithChildrenComponentProps
  extends ReportsComponentProps,
    CommonTableComponentProps {
  onColumnsUpdate?: (columns: AppTabularColumn[]) => void;
  visibleColumns?: string[];
  hiddenRows?: Set<string>;
  selectedRowsForHiding?: string[];
  onRowSelectionForHiding?: (selectedRows: string[]) => void;
  showOnlyFilteredRows?: string[];
  onClearShowOnlyFiltered?: () => void;
  isShowOnlyFilteredMode?: boolean;
}

const defaultParentSortBy = [{ id: 'category_name', desc: false }];

export const TableSummaryComponent = ({
  onRowSelectionChange,
  enableRowSelection = false,
  enableMultiRowSelection,
  filterSelection,
  reportName,
  reportId = '',
  totalReportName,
  enabled,
  height,
  defaultSortBy,
  parseMeasuresColumns,
  parseColumns,
  startColumns,
  endColumns,
  showTotal = false,
  //onFlatData,
  className,
  idField = 'id',
  setInvalidateQuery,
  enableStickyFirstColumn = false,
  suppressRowClickSelection,
  onColumnsUpdate,
  hiddenRows = new Set(),
  selectedRowsForHiding = [],
  onRowSelectionForHiding,
  showOnlyFilteredRows = [],
  onClearShowOnlyFiltered,
  isShowOnlyFilteredMode = false,
}: TableWithChildrenComponentProps) => {
  const [sortBy, setSortBy] = useState<SortBy[] | undefined>(defaultSortBy);
  const {
    data: childrenData,
    isFetching,
    invalidateQuery: invalidateQueryChildren,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDataProviderPaginated<any>(
    reportName,
    filterSelection,
    sortBy,
    enabled,
    reportId,
  );
  // To get parent data (category level) we only need category level in scenarioPlannerHier filter.
  const parentFilterSelection = useMemo(() => {
    return filterSelection.map((i) => {
      const ret = { ...i };
      if (ret.id === FILTER_NAMES.SCENARIOPLANNERPRODHIER) {
        ret.values = i.values.filter((x) => x.value === FILTER_NAMES.CATEGORY);
      }
      return ret;
    });
  }, [filterSelection]);

  const { data: parentData, invalidateQuery: invalidateQueryParent } =
    useDataProvider<any>(
      reportName,
      parentFilterSelection,
      defaultParentSortBy,
      enabled,
      true,
      reportId,
    );
  const {
    data: total,
    invalidateQuery: invalidateQueryTotal,
    isFetching: totalDataIsFetching,
  } = useDataProvider<any>(
    totalReportName,
    filterSelection,
    undefined,
    enabled,
    true,
    reportId,
  );
  const totalData = useMemo(() => {
    // This will ensure totals are updated with new values even if the filters are the same.
    if (totalDataIsFetching) {
      return undefined;
    }
    if (total) {
      // Category column must not rendered. We keep category column in back-end to reuse same report for exporting.
      const columns = total.columns.filter((i) => i.id !== 'category_name');
      return { ...total, columns };
    } else {
      return total;
    }
  }, [totalDataIsFetching, total]);

  // Generate unique stable keys for React reconciliation
  const generateUniqueKey = useCallback(
    (row: any, type: 'parent' | 'child', index: number, parentId?: string) => {
      if (type === 'parent') {
        return `parent_${row.category_id || row.id || index}_${row.category_name || 'unknown'}`;
      } else {
        return `child_${parentId}_${row.id || index}_${row.name || 'unknown'}`;
      }
    },
    [],
  );

  // STABLE data processing - prevent infinite re-renders with proper unique keys
  const data: AppTabularDto<any> | undefined = useMemo(() => {
    if (!parentData || !childrenData?.pages) {
      return undefined;
    }
    // No matter the number of pages, we combine all the rows in a single list.
    const childRows = childrenData.pages.flatMap((page) => page.rows);
    const page1 = childrenData.pages[0];
    const groupedRows: any[] = [];

    parentData.rows.forEach((parentRow, parentIndex) => {
      // Generate unique stable ID for parent rows
      const parentStableId =
        parentRow.category_id || parentRow.id || `parent_${parentIndex}`;
      const parentUniqueKey = generateUniqueKey(
        parentRow,
        'parent',
        parentIndex,
      );

      const newParentRow = {
        ...parentRow,
        name: parentRow.category_name,
        id: parentStableId,
        originalId: parentRow.id || parentRow.category_id,
        rowType: 'parent',
        [idField]: parentStableId,
        // Use unique key that includes category info to prevent conflicts
        stableKey: parentUniqueKey,
        // Add React key for reconciliation
        key: parentUniqueKey,
      };

      groupedRows.push(newParentRow);

      // Find children that belong to this parent
      const subRows = childRows.filter((childRow: any) => {
        return childRow.category_id === (parentRow.category_id || parentRow.id);
      });

      // Create child rows with unique stable IDs that include parent context
      newParentRow.children = subRows.map((childRow, childIndex) => {
        // Create unique child ID that includes parent context to avoid conflicts
        const childUniqueId = `${parentStableId}_${childRow.id || childIndex}`;
        const childUniqueKey = generateUniqueKey(
          childRow,
          'child',
          childIndex,
          parentStableId,
        );

        return {
          ...childRow,
          rowType: 'child',
          name: childRow.name,
          // Use the original child ID for business logic
          id: childRow.id,
          // Store unique ID for React/selection purposes
          uniqueId: childUniqueId,
          originalId: childRow.id,
          parentId: parentStableId,
          categoryId: parentRow.category_id || parentRow.id,
          [idField]: childRow.id, // Keep original ID for idField
          stableKey: childUniqueKey,
          // Add React key for reconciliation
          key: childUniqueKey,
        };
      });
    });

    return { ...page1, rows: groupedRows };
  }, [parentData, childrenData, idField, generateUniqueKey]);

  useEffect(() => {
    if (onColumnsUpdate && totalData?.measureColumns) {
      const availableColumns = totalData.measureColumns.filter(
        (col: any) => col.name && col.name.trim() !== '',
      );
      onColumnsUpdate(availableColumns);
    }
  }, [totalData?.measureColumns, onColumnsUpdate]);

  const invalidate = useCallback(async () => {
    await Promise.all([
      invalidateQueryTotal(),
      invalidateQueryChildren(),
      invalidateQueryParent(),
    ]);
  }, [invalidateQueryTotal, invalidateQueryChildren, invalidateQueryParent]);

  useEffect(() => {
    if (setInvalidateQuery) {
      setInvalidateQuery({ invalidateQuery: invalidate });
    }
  }, [invalidate, setInvalidateQuery]);

  return (
    <DataTableFlatWithHideRowsComponent
      data={data}
      totalData={totalData}
      isFetching={isFetching || totalDataIsFetching}
      parseColumns={parseColumns}
      parseMeasuresColumns={parseMeasuresColumns}
      startColumns={startColumns}
      endColumns={endColumns}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      sortBy={sortBy}
      setSortBy={setSortBy}
      className={className}
      showTotal={showTotal}
      height={height}
      idField={idField}
      enableRowSelection={enableRowSelection}
      enableMultiRowSelection={enableMultiRowSelection}
      onRowSelectionChange={onRowSelectionChange}
      enableStickyFirstColumn={enableStickyFirstColumn}
      suppressRowClickSelection={suppressRowClickSelection}
      expandRows={true}
      hiddenRows={hiddenRows}
      selectedRowsForHiding={selectedRowsForHiding}
      onRowSelectionForHiding={onRowSelectionForHiding}
      showOnlyFilteredRows={showOnlyFilteredRows}
      onClearShowOnlyFiltered={onClearShowOnlyFiltered}
      isShowOnlyFilteredMode={isShowOnlyFilteredMode}
    />
  );
};
